import { Calendar, DollarSign, Store, Tag, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import type { Database } from '../../lib/database.types';

type Receipt = Database['public']['Tables']['receipts']['Row'] & {
  categories?: Database['public']['Tables']['categories']['Row'];
};

interface ReceiptListProps {
  receipts: Receipt[];
  onSelectReceipt: (receipt: Receipt) => void;
  onRetry?: () => void;
}

export default function ReceiptList({ receipts, onSelectReceipt, onRetry }: ReceiptListProps) {
  const [retryingId, setRetryingId] = useState<string | null>(null);

  async function handleRetry(receipt: Receipt, e: React.MouseEvent) {
    e.stopPropagation();

    if (retryingId) return;

    setRetryingId(receipt.id);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/parse-receipt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          receiptId: receipt.id,
          fileId: receipt.telegram_file_id,
          chatId: 0,
          manualRetry: true,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        alert(`Retry failed: ${error.error || 'Unknown error'}`);
      }

      setTimeout(() => {
        if (onRetry) onRetry();
      }, 2000);
    } catch (error) {
      console.error('Error retrying receipt:', error);
      alert('Failed to retry receipt. Check console for details.');
    } finally {
      setRetryingId(null);
    }
  }

  if (receipts.length === 0) {
    return (
      <div className="p-12 text-center">
        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Store className="w-8 h-8 text-slate-400" />
        </div>
        <h3 className="text-lg font-medium text-slate-800 mb-2">No receipts yet</h3>
        <p className="text-slate-600">
          Send a receipt photo to your Telegram bot to get started
        </p>
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    const styles = {
      parsed: 'bg-emerald-100 text-emerald-700',
      pending: 'bg-amber-100 text-amber-700',
      failed: 'bg-red-100 text-red-700',
      verified: 'bg-blue-100 text-blue-700',
      duplicate: 'bg-orange-100 text-orange-700',
    };
    return styles[status as keyof typeof styles] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="overflow-x-auto -mx-3 sm:mx-0">
      <table className="w-full min-w-[640px]">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
              Date
            </th>
            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
              Merchant
            </th>
            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider hidden md:table-cell">
              Category
            </th>
            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
              Amount
            </th>
            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
              Status
            </th>
            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider hidden lg:table-cell">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-slate-200">
          {receipts.map((receipt) => (
            <tr
              key={receipt.id}
              onClick={() => onSelectReceipt(receipt)}
              className="hover:bg-slate-50 cursor-pointer transition-colors"
            >
              <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-slate-400 hidden sm:block" />
                  <span className="text-xs sm:text-sm text-slate-800">
                    {receipt.receipt_date
                      ? new Date(receipt.receipt_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                      : 'N/A'}
                  </span>
                </div>
              </td>
              <td className="px-3 sm:px-6 py-4">
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <Store className="w-3 h-3 sm:w-4 sm:h-4 text-slate-400 hidden sm:block" />
                  <span className="text-xs sm:text-sm font-medium text-slate-800 truncate max-w-[120px] sm:max-w-none">
                    {receipt.merchant_name || 'Unknown'}
                  </span>
                </div>
              </td>
              <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden md:table-cell">
                <div className="flex items-center space-x-2">
                  <Tag className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-600">
                    {receipt.categories?.name || 'Uncategorized'}
                  </span>
                </div>
              </td>
              <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 text-slate-400 hidden sm:block" />
                  <span className="text-xs sm:text-sm font-semibold text-slate-800">
                    {receipt.total_amount ? `${receipt.currency || 'USD'} ${receipt.total_amount.toFixed(2)}` : 'N/A'}
                  </span>
                </div>
              </td>
              <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                <span
                  className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(
                    receipt.status
                  )}`}
                >
                  {receipt.status}
                </span>
              </td>
              <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                {receipt.status === 'failed' && (
                  <button
                    onClick={(e) => handleRetry(receipt, e)}
                    disabled={retryingId === receipt.id}
                    className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {retryingId === receipt.id ? (
                      <>
                        <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                        Retrying...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Retry
                      </>
                    )}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
